import React from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import { formatDistanceToNow } from "date-fns";

interface JobData {
  id: string;
  job_type: string;
  status: string;
  filename: string;
  created_at: string;
  completed_at?: string;
  results?: {
    total?: number;
    processed?: number;
    created?: number;
    updated?: number;
    exported?: number;
    errors?: number;
  };
}

interface JobHistorySelectorProps {
  jobs: JobData[];
  selectedJobId: string;
  onPickJob: (jobId: string) => void;
}

const JobHistorySelector: React.FC<JobHistorySelectorProps> = ({
  jobs,
  selectedJobId,
  onPickJob,
}) => {
  const getJobLabel = (job: JobData) => {
    const timeAgo = formatDistanceToNow(new Date(job.created_at), {
      addSuffix: true,
    });
    const type = job.job_type === "import" ? "Import" : "Export";
    return `${type} - ${job.filename} (${timeAgo})`;
  };

  const getJobSummary = (job: JobData) => {
    if (job.status === "completed" && job.results) {
      if (job.job_type === "import") {
        return `${job.results.processed || 0} processed, ${
          job.results.created || 0
        } created, ${job.results.updated || 0} updated`;
      } else {
        return `${job.results.exported || 0} exported`;
      }
    }
    return job.status;
  };

  if (jobs.length === 0) {
    return null;
  }

  return (
    <FormControl size="small" sx={{ minWidth: 300 }}>
      <InputLabel>Job History</InputLabel>
      <Select
        value={selectedJobId}
        label="Job History"
        onChange={(e) => {
          const value = e.target.value;
          onPickJob(value);
        }}
      >
        <MenuItem value="">
          <em>Current Job</em>
        </MenuItem>
        {jobs.map((job) => (
          <MenuItem key={job.id} value={job.id}>
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" noWrap sx={{ flex: 1, mr: 1 }}>
                  {getJobLabel(job)}
                </Typography>
                <Chip
                  label={job.status.toUpperCase()}
                  size="small"
                  color={
                    job.status === "completed"
                      ? "success"
                      : job.status === "failed"
                      ? "error"
                      : "default"
                  }
                  variant="outlined"
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {getJobSummary(job)}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default JobHistorySelector;
